import React, {useEffect} from "react";
import Slider from "./section/slider";
import Statistics from "./section/statistics";
import LatestNews from "./section/latest-news";
import NewsletterSubscription from "./section/newsletter-subscription";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBinoculars,
    faChartLine,
    faListUl,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import "./styles.scss";
import {
    Box,
    Typography,
    Container,
    Grid,
    Stack,
    Hidden,
    useMediaQuery,
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {getHomePageData} from "../../../store/home-page/actions";
import {useTranslation} from "react-i18next";
import {grey} from "@mui/material/colors";
import shape1 from "../../../assets/images/shape1.png";
import {useTheme} from "@mui/material/styles";
import Title1 from "../../../components/base/Title1";
import homeImage1 from "../../../assets/images/home-image-1.png";
import ReadMore from "../../../components/read-more/read-more";
import {useHistory} from "react-router-dom";
import Partners from "./section/Partners";
import {RootState} from "../../../store";
import { getStorageValue } from "../../../utils/localStorageState";

const Home: React.FC = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const theme = useTheme();
    const history = useHistory();
    const langCode = getStorageValue("language", "hr");

    const smBreakpoints = useMediaQuery(theme.breakpoints.up("sm"));
    const mdBreakpoints = useMediaQuery(theme.breakpoints.up("md"));

    useEffect(() => {
        dispatch(getHomePageData());
    }, [dispatch, langCode]);

    const homePageData = useSelector((state: RootState) => state.homePage.data);
    let mission = homePageData.data.attributes?.missionHr;
    let vision = homePageData.data.attributes?.visionHr;
    let goals = homePageData.data.attributes?.goalsHr;
    let additionalText = homePageData.data.attributes?.additionalTextHr;
    let additionalTextBullets = homePageData.data.attributes?.additionalTextBulletsHr;
    let tenders = homePageData?.data.attributes?.tenders;
    let calls = homePageData?.data.attributes?.calls;
    let financeSupport = homePageData?.data.attributes?.financeSupport;
    let employed = homePageData?.data.attributes?.employed;
    let statistic1 = homePageData?.data.attributes?.statistic1;
    let statistic2 = homePageData?.data.attributes?.statistic2;
    let statistic3 = homePageData?.data.attributes?.statistic3;
    let statistic4 = homePageData?.data.attributes?.statistic4;
    const additionalTextImage = homePageData.data.attributes?.additionalTextImage;
    if ('en' === langCode) {
        mission = homePageData.data.attributes?.missionEn;
        vision = homePageData.data.attributes?.visionEn;
        goals = homePageData.data.attributes?.goalsEn;
        additionalText = homePageData.data.attributes?.additionalTextEn;
        additionalTextBullets = homePageData.data.attributes?.additionalTextBulletsEn;
        statistic1 = homePageData?.data.attributes?.statistic1En;
        statistic2 = homePageData?.data.attributes?.statistic2En;
        statistic3 = homePageData?.data.attributes?.statistic3En;
        statistic4 = homePageData?.data.attributes?.statistic4En;
    }
    if (!tenders) {
        tenders = 0;
    }
    if (!calls) {
        calls = 0;
    }
    if (!financeSupport) {
        financeSupport = 0;
    }
    if (!employed) {
        employed = 0;
    }
    if (!statistic1) {
        statistic1 = '';
    }
    if (!statistic2) {
        statistic2 = '';
    }
    if (!statistic3) {
        statistic3 = '';
    }
    if (!statistic4) {
        statistic4 = '';
    }

    return (
        <main>
            <Container>
                <Slider/>
                <Box paddingTop={2}>
                    <Title1 textAlign="center">
                        {t("HOME_MISSION_VISION_GOALS")}
                    </Title1>
                    <Grid container spacing={3} alignItems="stretch" mt={4} ml={0} sx={{ width: '100%' }}>
                        {[
                            {
                                icon: (
                                    <FontAwesomeIcon
                                        icon={faListUl}
                                        style={{
                                            color: "rgb(0, 151, 209)",
                                            fontSize: 34,
                                        }}
                                    />
                                ),
                                title: "HOME_MISSION_TITLE",
                                text: mission,
                            },
                            {
                                icon: (
                                    <FontAwesomeIcon
                                        icon={faBinoculars}
                                        style={{
                                            color: "rgb(0, 151, 209)",
                                            fontSize: 34,
                                        }}
                                    />
                                ),
                                title: "HOME_VISION_TITLE",
                                text: vision,
                            },
                            {
                                icon: (
                                    <FontAwesomeIcon
                                        icon={faChartLine}
                                        style={{
                                            color: "rgb(0, 151, 209)",
                                            fontSize: 34,
                                        }}
                                    />
                                ),
                                title: "HOME_GOALS_TITLE",
                                text: goals,
                            },
                        ].map((item) => (
                            <Grid item sm={12} md={4} sx={{ display: "flex", width: '100%' }} key={item.title} style={{ paddingLeft: mdBreakpoints ? undefined : 0  }}>
                                <Box
                                    sx={{
                                        p: 3,
                                        border: 1,
                                        borderRadius: 1,
                                        borderColor: grey[200],
                                        cursor: "pointer",
                                        ":hover": {
                                            boxShadow: "1px 0px 52px -15px rgba(0,0,0,0.15)",
                                        },
                                        width: '100%',
                                    }}
                                >
                                    {item.icon}
                                    <Typography variant="h5" sx={{my: 4}} fontWeight={700}>
                                        {t(item.title)}
                                    </Typography>
                                    <Typography variant="subtitle1" color={grey[500]}>
                                        {item.text}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <Statistics
                    tenders={tenders}
                    calls={calls}
                    financeSupport={financeSupport}
                    employed={employed}
                    text1={statistic1}
                    text2={statistic2}
                    text3={statistic3}
                    text4={statistic4}
                />
                <Stack pb={6}>
                    <Title1>{t("HOME_SUPPORT_TITLE")}</Title1>
                    <Grid container spacing={2} py={smBreakpoints ? 6 : 2}>
                        <Grid item sm={6}>
                            <Typography variant="subtitle1" color={grey[600]}>
                                {additionalText}
                            </Typography>
                            <Stack spacing={2} my={4}>
                                {(additionalTextBullets ? Object.values(additionalTextBullets) : []).map((listItem: string) => (
                                    <Box key={listItem} alignItems="center" display="flex" pt={1}>
                                        <FontAwesomeIcon
                                            icon={faCheckCircle}
                                            style={{
                                                fontSize: 36,
                                                color: theme.palette.secondary.main,
                                            }}
                                        />
                                        <Typography ml={2} variant="h6" fontWeight={700}>
                                            {listItem}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item sm={6}>
                            <Box sx={{position: "relative"}}>
                                <img
                                    src={additionalTextImage ? additionalTextImage : homeImage1}
                                    // src="https://picsum.photos/636/451"
                                    style={{width: "100%"}}
                                    alt="support section"
                                />
                                <Hidden mdDown>
                                    <img
                                        src={shape1}
                                        alt="shape1"
                                        style={{
                                            position: "absolute",
                                            top: -100,
                                            right: 0,
                                            width: 190,
                                            height: 190,
                                        }}
                                    />
                                </Hidden>
                                <Hidden mdUp>
                                    <img
                                        src={shape1}
                                        alt="shape1"
                                        style={{
                                            position: "absolute",
                                            top: -60,
                                            right: 0,
                                            width: 110,
                                            height: 110,
                                        }}
                                    />
                                </Hidden>
                            </Box>
                        </Grid>
                    </Grid>
                    <ReadMore onClick={() => history.push('en' === langCode ? '/support' : '/podrska')}/>
                </Stack>
                <LatestNews/>
                <Partners/>
            </Container>
            <NewsletterSubscription/>
        </main>
    );
};

export default React.memo(Home);
